import type {
  AnimationPreset,
  AnimationStep,
  CaptionsElement,
  SceneDocument,
  SceneElement,
} from "../scene/schema";
import { type CaptionPage, type CaptionWord, captionPageAt } from "./captions";

/**
 * Animations as pure functions of time. The renderer draws exactly these
 * states, and the video export uses them to find out which frames differ
 * (only changing frames are rendered, docs/05-video-und-untertitel.md §5).
 */

export interface AnimationState {
  opacity: number;
  /** Offset in document px. */
  x: number;
  y: number;
  scale: number;
  /** Degrees. */
  rotate: number;
  /** Blur radius in px. */
  blur: number;
  /** Typewriter: share of the text that is visible (null = all). */
  reveal: number | null;
}

export const REST_STATE: AnimationState = {
  opacity: 1,
  x: 0,
  y: 0,
  scale: 1,
  rotate: 0,
  blur: 0,
  reveal: null,
};

export function isRestState(state: AnimationState): boolean {
  return (
    state.opacity === 1 &&
    state.x === 0 &&
    state.y === 0 &&
    state.scale === 1 &&
    state.rotate === 0 &&
    state.blur === 0 &&
    state.reveal === null
  );
}

const clamp01 = (value: number) => Math.max(0, Math.min(1, value));
export const easeOutCubic = (p: number) => 1 - (1 - p) ** 3;
export const easeInCubic = (p: number) => p ** 3;
/** Overshoot ("pop"): 0 → ~1.1 → 1. */
export const easeOutBack = (p: number) => {
  const c1 = 1.70158;
  const c3 = c1 + 1;
  return 1 + c3 * (p - 1) ** 3 + c1 * (p - 1) ** 2;
};

/** Bouncing landing (0 → 1 with three bounces), like CSS "easeOutBounce". */
export const easeOutBounce = (p: number) => {
  const n = 7.5625;
  const d = 2.75;
  if (p < 1 / d) return n * p * p;
  if (p < 2 / d) return n * (p - 1.5 / d) ** 2 + 0.75;
  if (p < 2.5 / d) return n * (p - 2.25 / d) ** 2 + 0.9375;
  return n * (p - 2.625 / d) ** 2 + 0.984375;
};
const easeOutQuint = (p: number) => 1 - (1 - p) ** 5;

export const ANIMATION_LABELS: Record<AnimationPreset, string> = {
  fade: "Einblenden",
  "slide-up": "Von unten",
  "slide-down": "Von oben",
  "slide-left": "Von rechts",
  "slide-right": "Von links",
  pop: "Pop",
  zoom: "Zoom",
  blur: "Unschärfe",
  typewriter: "Schreibmaschine",
  "fly-left": "Reinfliegen von rechts",
  "fly-right": "Reinfliegen von links",
  "fly-up": "Reinfliegen von unten",
  "fly-down": "Reinfliegen von oben",
  drop: "Fallen & aufprallen",
  spin: "Reindrehen",
};

export const OUT_ANIMATION_LABELS: Record<AnimationPreset, string> = {
  fade: "Ausblenden",
  "slide-up": "Nach oben",
  "slide-down": "Nach unten",
  "slide-left": "Nach links",
  "slide-right": "Nach rechts",
  pop: "Pop",
  zoom: "Zoom",
  blur: "Unschärfe",
  typewriter: "Schreibmaschine",
  "fly-left": "Rausfliegen nach links",
  "fly-right": "Rausfliegen nach rechts",
  "fly-up": "Rausfliegen nach oben",
  "fly-down": "Rausfliegen nach unten",
  drop: "Fallen",
  spin: "Wegdrehen",
};

/** Easing of an entrance per preset (exits accelerate: ease-in). */
function entranceEasing(preset: AnimationPreset): (p: number) => number {
  if (preset === "pop" || preset === "spin") return easeOutBack;
  if (preset === "drop") return easeOutBounce;
  if (preset.startsWith("fly-")) return easeOutQuint;
  return easeOutCubic;
}

/** Applies one step with progress `q` (0 = hidden, 1 = at rest). `direction` −1 = exit. */
function applyStep(
  state: AnimationState,
  preset: AnimationPreset,
  q: number,
  direction: 1 | -1,
  size: { width: number; height: number },
  isText: boolean,
): AnimationState {
  const hidden = 1 - q;
  const distance = Math.min(240, Math.max(40, Math.min(size.width, size.height) * 0.6));
  const next = { ...state };
  switch (preset) {
    case "fade":
      next.opacity *= q;
      break;
    case "slide-up":
      next.y += hidden * distance * direction;
      next.opacity *= clamp01(q * 1.6);
      break;
    case "slide-down":
      next.y -= hidden * distance * direction;
      next.opacity *= clamp01(q * 1.6);
      break;
    case "slide-left":
      next.x += hidden * distance * direction;
      next.opacity *= clamp01(q * 1.6);
      break;
    case "slide-right":
      next.x -= hidden * distance * direction;
      next.opacity *= clamp01(q * 1.6);
      break;
    case "pop":
      next.scale *= 0.4 + 0.6 * q;
      next.opacity *= clamp01(q * 2.5);
      break;
    case "zoom":
      next.scale *= direction === 1 ? 0.6 + 0.4 * q : 1 + 0.4 * hidden;
      next.opacity *= q;
      break;
    case "blur":
      next.blur += hidden * 24;
      next.opacity *= q;
      break;
    case "typewriter":
      if (isText) next.reveal = Math.min(next.reveal ?? 1, q);
      else next.opacity *= q;
      break;
    case "fly-left":
    case "fly-right":
    case "fly-up":
    case "fly-down": {
      // From outside the picture: far enough for elements of any size.
      const far = Math.max(900, Math.max(size.width, size.height) * 1.3);
      if (preset === "fly-left") next.x += hidden * far * direction;
      if (preset === "fly-right") next.x -= hidden * far * direction;
      if (preset === "fly-up") next.y += hidden * far * direction;
      if (preset === "fly-down") next.y -= hidden * far * direction;
      next.opacity *= clamp01(q * 4);
      break;
    }
    case "drop":
      next.y -= hidden * Math.max(360, size.height * 1.6) * direction;
      next.opacity *= clamp01(q * 5);
      break;
    case "spin":
      next.rotate -= hidden * 140 * direction;
      next.scale *= Math.max(0, 0.2 + 0.8 * q);
      next.opacity *= clamp01(q * 2);
      break;
  }
  return next;
}

function stepProgress(step: AnimationStep, elapsed: number, duration: number): number {
  return clamp01(elapsed / Math.max(1, duration)) * (step.duration > 0 ? 1 : 0);
}

/**
 * State of an element at time `t`, or null when it is not visible then.
 * `animate: false` shows every visible element at rest (editing while paused).
 */
export function elementStateAt(
  element: SceneElement,
  t: number,
  options: { animate: boolean } = { animate: true },
): AnimationState | null {
  const range = element.time;
  if (range && (t < range.start || t >= range.end)) return null;
  const animation = element.animation;
  if (!options.animate || !animation) return REST_STATE;
  const start = range?.start ?? 0;
  const end = range?.end ?? Number.POSITIVE_INFINITY;
  const span = end - start;
  let inDuration = animation.in?.duration ?? 0;
  let outDuration = Number.isFinite(end) ? (animation.out?.duration ?? 0) : 0;
  // Entrance and exit share the visible time when it is short.
  if (inDuration + outDuration > span) {
    const factor = span / (inDuration + outDuration);
    inDuration *= factor;
    outDuration *= factor;
  }
  let state = REST_STATE;
  const size = { width: element.width, height: element.height };
  const isText = element.type === "text";
  if (animation.in && t - start < inDuration) {
    const p = stepProgress(animation.in, t - start, inDuration);
    const q = entranceEasing(animation.in.preset)(p);
    state = applyStep(
      state,
      animation.in.preset,
      animation.in.preset === "typewriter" ? p : q,
      1,
      size,
      isText,
    );
  }
  if (animation.out && end - t < outDuration) {
    const p = stepProgress(animation.out, end - t, outDuration);
    const q = 1 - easeInCubic(1 - p);
    state = applyStep(
      state,
      animation.out.preset,
      animation.out.preset === "typewriter" ? p : q,
      -1,
      size,
      isText,
    );
  }
  if (animation.loop) {
    const local = t - start;
    if (animation.loop === "pulse")
      state = {
        ...state,
        scale: state.scale * (1 + 0.035 * Math.sin((local / 1400) * Math.PI * 2)),
      };
    if (animation.loop === "float") {
      const amplitude = Math.min(24, Math.max(6, element.height * 0.05));
      state = { ...state, y: state.y - amplitude * Math.sin((local / 2600) * Math.PI * 2) };
    }
    if (animation.loop === "wiggle")
      state = { ...state, rotate: state.rotate + 2.5 * Math.sin((local / 700) * Math.PI * 2) };
  }
  return state;
}

// ---------------------------------------------------------------------------
// Captions
// ---------------------------------------------------------------------------

export interface CaptionWordState {
  word: CaptionWord;
  visible: boolean;
  /** Currently spoken. */
  active: boolean;
  spoken: boolean;
  opacity: number;
  scale: number;
  y: number;
  /** Typewriter: visible share of the letters. */
  reveal: number | null;
}

export interface CaptionFrame {
  page: CaptionPage;
  opacity: number;
  y: number;
  lines: CaptionWordState[][];
}

/** What the captions element shows at `t` (null: nothing). */
export function captionFrameAt(
  element: CaptionsElement,
  pages: readonly CaptionPage[],
  t: number,
): CaptionFrame | null {
  const page = captionPageAt(pages, t);
  if (!page) return null;
  const { preset, duration } = element.captionAnimation;
  const style = element.style;
  let opacity = 1;
  let y = 0;
  if (preset === "fade" || preset === "slide-up") {
    const fadeIn = clamp01((t - page.start) / duration);
    const fadeOut = clamp01((page.end - t) / Math.min(duration, 160));
    opacity = Math.min(easeOutCubic(fadeIn), fadeOut);
    if (preset === "slide-up") y = (1 - easeOutCubic(fadeIn)) * style.fontSize * 0.5;
  }
  const words = page.lines.flat();
  const lines = page.lines.map((line) =>
    line.map((word): CaptionWordState => {
      const index = words.indexOf(word);
      const next = words[index + 1];
      // Short gaps between words keep the highlight on the previous one.
      const activeEnd = next && next.start - word.end < 350 ? next.start : word.end;
      const spoken = t >= word.start;
      const active = spoken && t < activeEnd;
      const elapsed = t - word.start;
      const ramp = clamp01(elapsed / 90);
      let scale = active ? 1 + (style.activeScale - 1) * ramp : 1;
      let wordOpacity = 1;
      let wordY = 0;
      let visible = true;
      let reveal: number | null = null;
      switch (preset) {
        case "pop": {
          visible = spoken;
          const p = clamp01(elapsed / duration);
          scale *= p < 1 ? 0.7 + 0.3 * easeOutBack(p) : 1;
          wordOpacity = clamp01(elapsed / (duration * 0.5));
          break;
        }
        case "word-by-word": {
          const last = words.findLast((candidate) => candidate.start <= t);
          visible = last === word;
          const p = clamp01(elapsed / duration);
          scale *= p < 1 ? 0.8 + 0.2 * easeOutBack(p) : 1;
          break;
        }
        case "typewriter":
          visible = spoken;
          reveal = clamp01(elapsed / Math.max(80, word.end - word.start));
          if (reveal >= 1) reveal = null;
          break;
        case "karaoke":
          wordOpacity = spoken ? 1 : style.upcomingOpacity;
          break;
        case "bounce":
          if (active) {
            const p = clamp01(elapsed / (duration * 1.6));
            wordY = -Math.sin(p * Math.PI) * style.fontSize * 0.18;
          }
          break;
        default:
          break;
      }
      return {
        word,
        visible,
        active,
        spoken,
        opacity: wordOpacity,
        scale,
        y: wordY,
        reveal,
      };
    }),
  );
  return { page, opacity, y, lines };
}

// ---------------------------------------------------------------------------
// Frame signatures (export: render only frames that change)
// ---------------------------------------------------------------------------

const r3 = (value: number) => Math.round(value * 1000) / 1000;

function stateKey(state: AnimationState | null): string {
  if (!state) return "-";
  if (isRestState(state)) return "r";
  return [
    state.opacity,
    state.x,
    state.y,
    state.scale,
    state.rotate,
    state.blur,
    state.reveal ?? -1,
  ]
    .map((value) => r3(value))
    .join(",");
}

/**
 * A string that changes whenever a layer of elements (the top-level `ids`,
 * one band of `videoStack`) looks different at `t`. Equal signatures mean
 * equal pictures, so the export reuses the frame.
 */
export function overlaySignature(
  doc: SceneDocument,
  pages: readonly CaptionPage[],
  t: number,
  layer: ReadonlySet<string>,
): string {
  const parts: string[] = [];
  const visit = (ids: readonly string[], depth: number) => {
    if (depth > 50) return;
    for (const id of ids) {
      const element = doc.elements[id];
      if (!element || element.hidden) continue;
      if (depth === 0 && !layer.has(id)) continue;
      if (element.type === "captions") {
        const frame = captionFrameAt(element, pages, t);
        if (!frame) {
          parts.push(`${id}:-`);
          continue;
        }
        const words = frame.lines
          .flat()
          .map((state) =>
            state.visible
              ? `${state.active ? "a" : ""}${r3(state.opacity)}/${r3(state.scale)}/${r3(state.y)}/${state.reveal === null ? "" : r3(state.reveal)}`
              : "h",
          )
          .join(" ");
        parts.push(`${id}:${frame.page.index}:${r3(frame.opacity)}:${r3(frame.y)}:${words}`);
        continue;
      }
      const state = elementStateAt(element, t);
      parts.push(`${id}:${stateKey(state)}`);
      if (state && element.type === "group") visit(element.children, depth + 1);
    }
  };
  visit(doc.pages[0]?.children ?? [], 0);
  return parts.join("|");
}
